import {
  buildAddTenorCatalogParams,
  parseAddTenorCatalogId,
} from "constants/addTenorTable";
import { formatDateForApi } from "./addTenorTableValidation";
import { isAddTenorGroupedRow } from "./addTenorRowExpansion";

/** Drop null/undefined (and empty strings) so the API receives omitted fields, not `null`. */
function omitEmptyFields(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value != null && value !== ""
    )
  );
}

function buildSingleTenorMonth(row) {
  const includeStartDate = Boolean(row.enable_start_date);
  const rowEnd = formatDateForApi(row.end_date);
  const rowStart = formatDateForApi(row.start_date);

  const month = omitEmptyFields({
    subleg_month: null,
    start_date: includeStartDate ? rowStart : null,
    end_date: rowEnd,
  });
  if (!month.end_date) {
    throw new Error("End date is required");
  }
  return month;
}

function buildGroupedTenorMonth(row) {
  const includeStartDate = Boolean(row.enable_start_date);
  const label = String(row.sub_leg ?? "").trim();
  const end = formatDateForApi(row.end_date);
  const start = includeStartDate ? formatDateForApi(row.start_date) : null;

  const month = omitEmptyFields({
    subleg_month: label || null,
    start_date: start,
    end_date: end,
  });
  if (!month.end_date) {
    throw new Error("End date is required");
  }
  return month;
}

/**
 * @param {object} row — Add Tenor table row
 * @param {string} [shortcutLabel]
 * @returns {TenorMonthPayload[]}
 */
export function buildTenorMonthsFromRow(row, shortcutLabel = "") {
  if (isAddTenorGroupedRow(row)) {
    return [buildGroupedTenorMonth(row, shortcutLabel)];
  }

  const months = Math.max(1, Number(row.months_in_tenor) || 1);
  if (months <= 1) {
    return [buildSingleTenorMonth(row, shortcutLabel)];
  }

  return [buildGroupedTenorMonth(row, shortcutLabel)];
}

/**
 * @param {object[]} rows — rows in one tenor group, sorted by sub_leg_index
 * @param {string} [shortcutLabel]
 * @returns {TenorMonthPayload[]}
 */
export function buildTenorMonthsFromRowGroup(rows, shortcutLabel = "") {
  const sorted = [...rows].sort(
    (a, b) =>
      Math.max(0, Number(a.sub_leg_index) || 0) -
      Math.max(0, Number(b.sub_leg_index) || 0)
  );
  const primary = sorted[0];
  if (!primary) return [];

  const months = Math.max(1, Number(primary.months_in_tenor) || 1);
  if (months <= 1) {
    return [buildSingleTenorMonth(primary, shortcutLabel)];
  }

  return sorted.map((row) => buildGroupedTenorMonth(row, shortcutLabel));
}

/**
 * @typedef {object} TenorMonthPayload
 * @property {string|null} [subleg_month]
 * @property {string|null} [start_date]
 * @property {string} end_date
 */

/**
 * @typedef {object} CreateTenorRequest
 * @property {number} product_id
 * @property {number} contract_id
 * @property {number} style_id
 * @property {number} period_id
 * @property {string} tenor
 * @property {string|null} [date]
 * @property {string|null} [underlying_tenor]
 * @property {TenorMonthPayload[]} tenor_months
 */

/**
 * Maps an Add Tenor table row to POST /option-rows/tenors body.
 * @param {object} row
 * @param {string} [shortcutLabel]
 * @returns {CreateTenorRequest}
 */
export function buildCreateTenorRequestFromRow(row, shortcutLabel = "") {
  const tenor = String(row.tenor_label ?? "").trim();
  const underlying = String(row.ul_tenor ?? "").trim() || null;

  const tenorDate = formatDateForApi(row.tenor_date);
  const catalog = buildAddTenorCatalogParams(row);
  if (!catalog) {
    throw new Error("Product, contract, and style are required");
  }

  return omitEmptyFields({
    product_id: catalog.product_id,
    contract_id: catalog.contract_id,
    style_id: catalog.style_id,
    period_id: parseAddTenorCatalogId(row.period_type_id),
    tenor,
    date: tenorDate,
    underlying_tenor: underlying,
    tenor_months: buildTenorMonthsFromRow(row, shortcutLabel),
  });
}

/**
 * @param {object[]} rows — rows in one tenor group
 * @param {string} [shortcutLabel]
 * @returns {CreateTenorRequest}
 */
function pcsKeyFromTenorRequest(body) {
  return `${body.product_id}|${body.contract_id}|${body.style_id}`;
}

/**
 * @param {object[][]} submitGroups — from groupAddTenorRowsForSubmit
 * @param {string} [shortcutLabel]
 * @returns {{ product_id: number, contract_id: number, style_id: number, tenors: CreateTenorRequest[] }}
 */
export function buildBatchCreateTenorRequestFromSubmitGroups(
  submitGroups,
  shortcutLabel = ""
) {
  if (!Array.isArray(submitGroups) || !submitGroups.length) {
    throw new Error("At least one tenor is required");
  }

  const tenors = submitGroups.map((group) =>
    buildCreateTenorRequestFromRowGroup(group, shortcutLabel)
  );
  const pcsKeys = new Set(tenors.map(pcsKeyFromTenorRequest));
  if (pcsKeys.size !== 1) {
    throw new Error("Batch save requires the same product, contract, and style");
  }

  const first = tenors[0];
  return {
    product_id: first.product_id,
    contract_id: first.contract_id,
    style_id: first.style_id,
    tenors,
  };
}

/**
 * @param {object[][]} submitGroups
 * @returns {boolean}
 */
export function submitGroupsShareProductContractStyle(submitGroups) {
  if (!Array.isArray(submitGroups) || submitGroups.length < 2) return false;
  try {
    buildBatchCreateTenorRequestFromSubmitGroups(submitGroups);
    return true;
  } catch {
    return false;
  }
}

export function buildCreateTenorRequestFromRowGroup(rows, shortcutLabel = "") {
  const sorted = [...rows].sort(
    (a, b) =>
      Math.max(0, Number(a.sub_leg_index) || 0) -
      Math.max(0, Number(b.sub_leg_index) || 0)
  );
  const primary = sorted[0];
  const tenor = String(primary?.tenor_label ?? "").trim();
  const underlying = String(primary?.ul_tenor ?? "").trim() || null;
  const tenorDate = formatDateForApi(primary?.tenor_date);
  const catalog = buildAddTenorCatalogParams(primary);
  if (!catalog) {
    throw new Error("Product, contract, and style are required");
  }

  return omitEmptyFields({
    product_id: catalog.product_id,
    contract_id: catalog.contract_id,
    style_id: catalog.style_id,
    period_id: parseAddTenorCatalogId(primary.period_type_id),
    tenor,
    date: tenorDate,
    underlying_tenor: underlying,
    tenor_months: buildTenorMonthsFromRowGroup(rows, shortcutLabel),
  });
}
